// Utilidades de estructura de cursos Vortex.
// Soporta dos formatos:
//  - Legado: course.lessons[] (plano, con campo `module`)
//  - Nuevo:  course.temas[]  (temas anidados, cada uno con lessons[] y examenes[])

export const PASS_THRESHOLD = 70;

// Convierte un curso legado (lessons planas) al formato nuevo (temas con lessons y examenes).
// Si el curso ya tiene `temas`, se devuelve tal cual.
export const normalizeCourse = (course) => {
  if (!course) return null;
  if (course.temas && Array.isArray(course.temas)) {
    // Migración: mover exámenes de nivel tema → nivel lección (formato anterior)
    const temas = course.temas.map((tema) => {
      if (tema.examenes && Array.isArray(tema.examenes) && tema.examenes.length > 0) {
        const lessons = (tema.lessons || []).map((l, li) => {
          if (l.examenes && Array.isArray(l.examenes)) return l;
          const exames = tema.examenes.filter((e) => e.lessonId === l.id);
          return exames.length > 0 ? { ...l, examenes: exames } : l;
        });
        const orphanExams = tema.examenes.filter((e) => !e.lessonId);
        if (orphanExams.length > 0 && lessons.length > 0) {
          lessons[0] = { ...lessons[0], examenes: [...(lessons[0].examenes || []), ...orphanExams] };
        }
        // Eliminar la clave tema.examenes (no undefined) para que set() no falle.
        const { examenes: _examenes, ...restTema } = tema;
        return { ...restTema, lessons };
      }
      return tema;
    });
    return { ...course, temas };
  }

  const lessons = Array.isArray(course.lessons) ? course.lessons : [];
  const groups = {};
  lessons.forEach((lesson) => {
    const moduleName = lesson.module || 'Módulo General';
    if (!groups[moduleName]) groups[moduleName] = [];
    groups[moduleName].push(lesson);
  });

  const temas = Object.entries(groups).map(([moduleName, moduleLessons], tIdx) => {
    const lessonsWithExams = moduleLessons.map((l) => ({
      ...l,
      examenes: l.quiz && Array.isArray(l.quiz) && l.quiz.length > 0
        ? [{ id: `e_${l.id}`, title: `Examen de la Lección`, order: 1, questions: l.quiz }]
        : [],
    }));
    return {
      id: `t${tIdx}`,
      title: moduleName,
      order: tIdx + 1,
      lessons: lessonsWithExams.map((l, li) => ({ ...l, order: li + 1 })),
    };
  });

  // El formato nuevo ya no usa examen final global: cada tema gestiona su examen_final.
  // Omitimos la clave (no `undefined`) para que set() de Firebase no falle.
  const { examen_final, ...rest } = course;
  return {
    ...rest,
    temas,
  };
};

export const getTemas = (course) => normalizeCourse(course)?.temas || [];
export const getAllLessons = (course) => getTemas(course).flatMap((t) => t.lessons || []);

// Exámenes de una lección (formato nuevo: lesson.examenes[])
export const getLessonExamenes = (lesson) => {
  if (!lesson) return [];
  if (Array.isArray(lesson.examenes)) return lesson.examenes;
  // Legado: deriva un examen a partir del quiz de la lección
  if (lesson.quiz && Array.isArray(lesson.quiz) && lesson.quiz.length > 0) {
    return [{ id: `e_${lesson.id}`, title: `Examen de la Lección`, order: 1, questions: lesson.quiz }];
  }
  return [];
};

// Examen final de un tema (formato: tema.examen_final con id/title/questions).
export const getTemaExamenFinal = (tema) => {
  const fe = tema?.examen_final;
  if (!fe || !Array.isArray(fe.questions) || fe.questions.length === 0) return null;
  return { id: fe.id || `final_${tema.id}`, title: fe.title || `Examen Final del Tema`, questions: fe.questions };
};

// Los exámenes de las lecciones de un tema están aprobados (>= umbral). Al cumplirse
// se desbloquea el examen final de ESE tema.
export const isTemaExamenesAprobados = (tema, progress) => {
  const examenes = (tema.lessons || []).flatMap(getLessonExamenes);
  if (examenes.length === 0) return false;
  const temaProg = progress?.temas?.[tema.id];
  return examenes.every((ex) => {
    const result = temaProg?.examenes?.[ex.id];
    return result && result.score >= PASS_THRESHOLD;
  });
};

// Un tema está completado cuando TODOS los exámenes de TODAS sus lecciones están aprobados
// (>= umbral) Y, si el tema tiene examen final, este también está aprobado.
export const isTemaCompletado = (tema, progress) => {
  if (!isTemaExamenesAprobados(tema, progress)) return false;
  const finalExam = getTemaExamenFinal(tema);
  if (!finalExam) return true;
  const result = progress?.temas?.[tema.id]?.examenes?.[finalExam.id];
  return !!(result && result.score >= PASS_THRESHOLD);
};

// Todos los temas están completados (incluidos sus exámenes finales).
export const isCourseCompletado = (course, progress) => {
  const temas = getTemas(course);
  if (temas.length === 0) return false;
  return temas.every((t) => isTemaCompletado(t, progress));
};

// Compatibilidad: alias de isCourseCompletado (sin examen final global del curso).
export const isExamenFinalLiberado = isCourseCompletado;

export const countCompletedTemas = (course, progress) =>
  getTemas(course).filter((t) => isTemaCompletado(t, progress)).length;

export const countLessons = (course) => getAllLessons(course).length;
export const countTemas = (course) => getTemas(course).length;
export const countExamenes = (course) =>
  getTemas(course).reduce(
    (acc, t) => acc + (t.lessons || []).reduce((a, l) => a + getLessonExamenes(l).length, 0),
    0
  );

// ── Resumen del curso (conteos mostrados en tarjetas, modal y admin) ──
// La fuente de verdad del contenido puede ser:
//   - course.temas[] embebido en RTDB (legado)
//   - Firestore vortexAcademyAreas/{areaKey} (nuevo, el contenido real vive ahí)
//   - resumen_override: valores fijados manualmente por el admin
export const EMPTY_RESUMEN = { temas: 0, lecciones: 0, miniExamenes: 0, examenes: 0, preguntas: 0, source: 'auto' };

// Conteo local derivado del contenido embebido del curso (course.temas/lessons).
export const localCourseResumen = (course) => {
  const temas = getTemas(course);
  const lessons = getAllLessons(course);
  return {
    temas: temas.length,
    lecciones: lessons.length,
    miniExamenes: countExamenes(course),
    examenes: temas.length,
    preguntas: lessons.reduce((acc, l) => acc + (Array.isArray(l.quiz) ? l.quiz.length : 0), 0),
    source: 'auto',
  };
};

// Resumen efectivo de un curso:
//  1) override manual del admin (course.resumen_override) — siempre gana
//  2) stats reales de Firestore (areaStats) — la fuente de verdad del contenido
//  3) conteo local del contenido embebido
export const resolveCourseResumen = (course, areaStats) => {
  const manual = course?.resumen_override && typeof course.resumen_override === 'object'
    ? course.resumen_override
    : null;
  if (manual) return { ...EMPTY_RESUMEN, ...manual, source: 'manual' };
  if (areaStats) {
    return {
      ...EMPTY_RESUMEN,
      temas: areaStats.temas || 0,
      lecciones: areaStats.lecciones || 0,
      miniExamenes: areaStats.miniExamenes || 0,
      preguntas: areaStats.preguntas || 0,
      examenes: areaStats.temas || 0, // cada tema del Lector tiene su examen final
      source: 'auto',
    };
  }
  return localCourseResumen(course);
};

// Un curso es visible para un perfil si NO tiene restricciones de jerarquía/cuerpo/especialidad
// o si el perfil coincide con al menos una de las etiquetas definidas.
const matchesTag = (courseTag, userValue) => {
  if (!courseTag) return true; // sin etiqueta = visible para todos
  if (!userValue) return false; // el curso restringe pero el usuario no tiene ese dato
  const tags = Array.isArray(courseTag) ? courseTag : [courseTag];
  return tags.some((t) => String(t).trim().toLowerCase() === String(userValue).trim().toLowerCase());
};

export const isCourseForProfile = (course, profile = {}) => {
  if (!course) return false;
  const { jerarquia, cuerpoServicio, especialidad } = profile;
  if (!matchesTag(course.jerarquia, jerarquia)) return false;
  if (!matchesTag(course.cuerpo, cuerpoServicio) && !matchesTag(course.cuerpoServicio, cuerpoServicio)) return false;
  if (!matchesTag(course.especialidad, especialidad)) return false;
  return true;
};

// Flashcards de una lección. Prioriza las flashcards explícitas (lesson.flashcards,
// formato { front, back } o { t, b }); si no las hay, las deriva del quiz /
// quizQuestions y de los puntos clave (key_points / kps).
export const getLessonFlashcards = (lesson) => {
  if (!lesson) return [];
  if (Array.isArray(lesson.flashcards) && lesson.flashcards.length > 0) {
    return lesson.flashcards
      .filter((f) => f && (f.front || f.t))
      .map((f, i) => ({
        id: f.id || `fc_${i}`,
        front: f.front || f.t || '',
        back: f.back || f.b || '',
      }));
  }
  const quiz = Array.isArray(lesson.quiz) ? lesson.quiz : (Array.isArray(lesson.quizQuestions) ? lesson.quizQuestions : []);
  const cards = [];
  quiz.forEach((q, i) => {
    if (!q || !q.question) return;
    const correct = q.options?.[q.correct_index];
    cards.push({
      id: `qz_${i}`,
      front: q.question,
      back: q.explanation ? `✔️ ${correct || ''}\n\n📖 ${q.explanation}` : (correct || 'Ver respuestas en el quiz'),
    });
  });
  if (cards.length) return cards;
  const kps = lesson.key_points || lesson.kps || [];
  return (Array.isArray(kps) ? kps : [])
    .filter(Boolean)
    .map((k, i) => {
      if (typeof k === 'string') return { id: `kp_${i}`, front: k, back: 'Punto clave de esta lección' };
      return { id: `kp_${i}`, front: k.t || k.question || '', back: k.b || k.answer || 'Punto clave de esta lección' };
    })
    .filter((c) => c.front);
};
